import * as React from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Box,
  Modal,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import styled from "styled-components";

import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";

import { updateUser } from "../actions/user";
import ProfileImage from "./Avatar";
import { signOut } from "../Api/index";
import { NAV } from "../navigation";

export const SIDEBAR_WIDTH = 248;
// The header matches this height, so their bottom lines meet
export const TOP_BAR_HEIGHT = 63;

// The Portfolio Manager's own blue, laid out like the Assets Register. The
// header uses these colours too, so the two read as one frame.
export const SIDEBAR_COLORS = {
  bg: "#355C80",
  bgRaised: "#2D4E6C",
  line: "rgba(255,255,255,0.12)",
  text: "#F1F5F9",
  textDim: "rgba(255,255,255,0.78)",
  textFaint: "rgba(255,255,255,0.6)",
  hover: "rgba(255,255,255,0.08)",
  active: "rgba(255,255,255,0.16)",
  // a lighter green than the brand's #17B859, so it shows up on the blue
  accent: "#7EE2A8",
};
const C = SIDEBAR_COLORS;

const StyleSidebar = styled.aside`
  overflow: hidden;
  grid-row: 1/-1;
`;

// eslint-disable-next-line react/prop-types
const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      px: 1.5,
      pt: 2.2,
      pb: 0.8,
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: "0.09em",
      textTransform: "uppercase",
      color: C.textFaint,
    }}
  >
    {children}
  </Typography>
);

const itemSx = (selected) => ({
  position: "relative",
  borderRadius: "6px",
  px: 1.5,
  py: 0.85,
  mb: 0.25,
  transition: "background-color 120ms ease",
  "&:hover": { backgroundColor: C.hover },
  "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: C.active },
  "&::before": selected
    ? {
        content: '""',
        position: "absolute",
        left: 0,
        top: 8,
        bottom: 8,
        width: 3,
        borderRadius: 2,
        backgroundColor: C.accent,
      }
    : {},
});

const iconSx = (active) => ({
  minWidth: 34,
  color: active ? C.accent : C.textDim,
  "& svg": { fontSize: 19 },
});

const labelProps = (selected) => ({
  sx: {
    fontSize: 13.5,
    fontWeight: selected ? 600 : 500,
    color: selected ? "#fff" : C.text,
    whiteSpace: "nowrap",
  },
});

// The "/investment/" dashboard also answers to "/investment"
const samePath = (a, b) => a.replace(/\/+$/, "") === b.replace(/\/+$/, "");

const inGroup = (item, path) =>
  !!item.children?.some((child) => samePath(child.path, path));

export default function SideBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const path = useLocation().pathname;

  const [logoutOpen, setLogoutOpen] = React.useState(false);

  const user = React.useMemo(() => {
    try {
      return JSON.parse(secureLocalStorage.getItem("profile"));
    } catch {
      return null;
    }
  }, []);

  const isApprover =
    user?.results?.portfolio_manager === "Administrator" ||
    user?.results?.portfolio_manager === "Approver";

  // Waiting for an approver; returned ones wait on whoever entered them
  const awaiting = useSelector(
    (state) =>
      (state.newInvestment || []).filter(
        (inv) => inv.approval_status !== "returned",
      ).length,
  );

  // NAV items under their section headings, in order of first appearance
  const sections = React.useMemo(() => {
    const out = [];
    NAV.filter((item) => !item.approversOnly || isApprover).forEach((item) => {
      let section = out.find((s) => s.label === item.section);
      if (!section) {
        section = { label: item.section, items: [] };
        out.push(section);
      }
      section.items.push(item);
    });
    return out;
  }, [isApprover]);

  // Dropdowns: the one holding the current page opens by itself
  const [openGroups, setOpenGroups] = React.useState({});
  React.useEffect(() => {
    const current = NAV.find((item) => inGroup(item, path));
    if (current) {
      setOpenGroups((open) =>
        open[current.label] ? open : { ...open, [current.label]: true },
      );
    }
  }, [path]);
  const toggleGroup = (label) =>
    setOpenGroups((open) => ({ ...open, [label]: !open[label] }));

  const logout = () => {
    const loggingOutId = toast.loading("Signing you out...");
    dispatch(updateUser(user?.results?._id, { login: "no", system: "none" }));

    setTimeout(() => {
      // Ends the shared login, so the other apps are logged out too
      signOut().catch(() => {});
      dispatch({ type: "LOGOUT" });
      toast.remove(loggingOutId);
      navigate("/investment/login");
    }, 1200);
  };

  const fullName = `${user?.results?.firstName || ""} ${
    user?.results?.lastName || ""
  }`.trim();
  const access = user?.results?.portfolio_manager || "-";

  const renderItem = (item) => {
    if (!item.children) {
      const selected = samePath(path, item.path);
      const badge = item.showAwaiting ? awaiting : undefined;
      return (
        <ListItemButton
          key={item.path}
          selected={selected}
          onClick={() => navigate(item.path)}
          sx={itemSx(selected)}
        >
          <ListItemIcon sx={iconSx(selected)}>{item.icon}</ListItemIcon>
          <ListItemText
            primaryTypographyProps={labelProps(selected)}
            primary={
              !badge ? (
                item.label
              ) : (
                // pending count right after the label
                <Box
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
                >
                  {item.label}
                  <Box
                    component="span"
                    aria-label={`${badge} awaiting approval`}
                    sx={{
                      minWidth: 22,
                      height: 22,
                      px: "7px",
                      borderRadius: "999px",
                      backgroundColor: "#FF3B30",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 0 0 2px rgba(255,255,255,0.2)",
                    }}
                  >
                    {badge > 99 ? "99+" : badge}
                  </Box>
                </Box>
              )
            }
          />
        </ListItemButton>
      );
    }

    const open = !!openGroups[item.label];
    const active = inGroup(item, path);
    return (
      <React.Fragment key={item.label}>
        <ListItemButton
          selected={active && !open}
          onClick={() => toggleGroup(item.label)}
          aria-expanded={open}
          sx={itemSx(active && !open)}
        >
          <ListItemIcon sx={iconSx(active)}>{item.icon}</ListItemIcon>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={labelProps(active)}
          />
          <ExpandMore
            sx={{
              fontSize: 18,
              color: C.textFaint,
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 160ms ease",
            }}
          />
        </ListItemButton>
        <Collapse in={open} timeout={160} unmountOnExit>
          <List
            disablePadding
            sx={{
              ml: "26px",
              pl: 1,
              mb: 0.5,
              borderLeft: `1px solid ${C.line}`,
            }}
          >
            {item.children.map((child) => {
              const selected = samePath(path, child.path);
              return (
                <ListItemButton
                  key={child.path}
                  selected={selected}
                  onClick={() => navigate(child.path)}
                  sx={{
                    borderRadius: "6px",
                    px: 1.25,
                    py: 0.55,
                    mb: 0.15,
                    transition: "background-color 120ms ease",
                    "&:hover": { backgroundColor: C.hover },
                    "&.Mui-selected, &.Mui-selected:hover": {
                      backgroundColor: C.active,
                    },
                  }}
                >
                  <ListItemText
                    primary={child.label}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: 12.5,
                        lineHeight: 1.35,
                        fontWeight: selected ? 600 : 400,
                        color: selected ? "#fff" : C.textDim,
                      },
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  return (
    <StyleSidebar>
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            height: "100vh",
            boxSizing: "border-box",
            border: "none",
            borderRight: `1px solid ${C.line}`,
            backgroundColor: C.bg,
            color: C.text,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Product */}
        <Box
          sx={{
            height: TOP_BAR_HEIGHT,
            px: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            borderBottom: `1px solid ${C.line}`,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              display: "grid",
              placeItems: "center",
              backgroundColor: C.active,
              color: C.accent,
            }}
          >
            <ShowChartOutlinedIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#fff",
                lineHeight: 1.2,
              }}
            >
              Portfolio Manager
            </Typography>
            <Typography
              sx={{ fontSize: 11, color: C.textFaint, lineHeight: 1.3 }}
            >
              Investment management
            </Typography>
          </Box>
        </Box>

        {/* Navigation */}
        <Box
          component="nav"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 1.25,
            pb: 2,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255,255,255,0.12)",
              borderRadius: 3,
            },
          }}
        >
          {sections.map((section) => (
            <React.Fragment key={section.label}>
              <SectionLabel>{section.label}</SectionLabel>
              <List disablePadding>{section.items.map(renderItem)}</List>
            </React.Fragment>
          ))}
        </Box>

        {/* Signed-in user */}
        <Box
          sx={{
            flexShrink: 0,
            borderTop: `1px solid ${C.line}`,
            backgroundColor: C.bgRaised,
            px: 1.5,
            py: 1.25,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <ProfileImage size={36} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                title={fullName}
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                  lineHeight: 1.25,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {fullName || "User"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11.5,
                  color: C.textDim,
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {access}
              </Typography>
            </Box>
            <Tooltip title="Sign out" placement="top">
              <IconButton
                onClick={() => setLogoutOpen(true)}
                size="small"
                aria-label="Sign out"
                sx={{
                  color: C.textDim,
                  borderRadius: "6px",
                  "&:hover": { color: "#fff", backgroundColor: C.hover },
                }}
              >
                <LogoutOutlinedIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            sx={{
              mt: 1,
              fontSize: 10.5,
              color: C.textFaint,
              letterSpacing: "0.02em",
            }}
          >
            Powered by Rosoftsavvy
          </Typography>
        </Box>
      </Drawer>

      {/* Sign-out confirmation */}
      <Modal
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        aria-labelledby="signout-title"
        aria-describedby="signout-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "88%", sm: 380 },
            borderRadius: "10px",
            bgcolor: "#fff",
            boxShadow: "0 24px 60px rgba(15,23,42,0.25)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 3, pt: 2.75, pb: 2 }}>
            <Typography
              id="signout-title"
              sx={{ fontSize: 16, fontWeight: 600, color: "#0F172A" }}
            >
              Sign out of Portfolio Manager?
            </Typography>
            <Typography
              id="signout-description"
              sx={{ mt: 0.75, fontSize: 13, color: "#475569", lineHeight: 1.5 }}
            >
              You will be signed out of all bank applications on this browser.
            </Typography>
          </Box>
          <Box
            sx={{
              px: 3,
              py: 1.75,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              backgroundColor: "#F8FAFC",
              borderTop: "1px solid #E2E8F0",
            }}
          >
            <Button
              onClick={() => setLogoutOpen(false)}
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                fontWeight: 500,
                color: "#334155",
                borderColor: "#CBD5E1",
                borderRadius: "6px",
                px: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={logout}
              variant="contained"
              size="small"
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "#116149",
                borderRadius: "6px",
                px: 2,
                "&:hover": { backgroundColor: "#0F523E" },
              }}
            >
              Sign out
            </Button>
          </Box>
        </Box>
      </Modal>
    </StyleSidebar>
  );
}
